import React, { useState, useEffect, useRef }  from 'react';
import { PostRequest, GetRequest } from 'components/common/index.js';
import { getAvailableThemes } from 'components/quote_builder_widget/themes';
import WidgetPreview from 'components/quote_builder_widget/WidgetPreview';
import CssEditorModal from 'components/quote_builder_widget/CssEditorModal';

const SAMPLE_TEMPLATE_IDENTIFIER = 'becf-12017';

// Themes that support custom CSS
const THEMES_WITH_CUSTOM_CSS = ['dark', 'default', 'minimal', 'compact'];

// Available font families for widget customization
const FONT_FAMILIES = [
  { value: '', label: 'Default (System)' },
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: 'Helvetica, Arial, sans-serif', label: 'Helvetica' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: 'Verdana, sans-serif', label: 'Verdana' },
  { value: 'Tahoma, sans-serif', label: 'Tahoma' },
  { value: '"Trebuchet MS", sans-serif', label: 'Trebuchet MS' },
  { value: '"Times New Roman", serif', label: 'Times New Roman' },
  { value: '"Courier New", monospace', label: 'Courier New' },
  { value: 'system-ui, sans-serif', label: 'System UI' },
];

const FormNew = ( { identifier, setMode } ) => {
  const domainInputRef = useRef();
  const initialFetchDone = useRef(false);
  const [domains, setDomains] = useState( [] );
  const [highlightColor, setHighlightColor] = useState(['#F44336']);
  const [selectedTheme, setSelectedTheme] = useState('default');
  const [backgroundColor, setBackgroundColor] = useState('#1a1a2e');
  const [fontFamily, setFontFamily] = useState('');
  const [customCss, setCustomCss] = useState('');
  const [isCssEditorOpen, setIsCssEditorOpen] = useState(false);
  const availableThemes = getAvailableThemes();

  const [addDomainFormVisible, setAddDomainFormVisible] = useState(false)
  const [addDomainFormHighlighed, setAddDomainFormHighlighed] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [successMessage, setSuccessMessage] = useState("")
  const [newDomain, setNewDomain] = useState("")
  const [savedIdentifier, setSavedIdentifier] = useState(identifier)
  const [previewKey, setPreviewKey] = useState(Date.now())

  const supportsCustomCss = THEMES_WITH_CUSTOM_CSS.includes(selectedTheme);

  // Send style updates to widget preview iframe for live preview
  const sendStyleUpdate = (type, data) => {
    const iframes = document.querySelectorAll('iframe');
    iframes.forEach((iframe) => {
      try {
        iframe.contentWindow.postMessage({ type, ...data }, '*');
      } catch (error) {
        // Ignore cross-origin errors
      }
    });
  };

  useEffect(() => {
    if (supportsCustomCss && savedIdentifier) {
      sendStyleUpdate('diecuttemplates.update_background_color', { color: backgroundColor });
    }
  }, [backgroundColor, supportsCustomCss, savedIdentifier]);

  useEffect(() => {
    if (supportsCustomCss && savedIdentifier) {
      sendStyleUpdate('diecuttemplates.update_font_family', { fontFamily: fontFamily });
    }
  }, [fontFamily, supportsCustomCss, savedIdentifier]);

  const fetchWidget = () => {
    if(identifier == undefined) { return }

    GetRequest({
      url: `/quote-builder/user-quote-builder-widgets/${identifier}` ,
      callback: (data) => {
        setDomains( () => data.domains );
        setHighlightColor( () => data.properties.highlight_color );
        setSelectedTheme( () => data.properties.theme || 'default' );
        setBackgroundColor( () => data.properties.background_color || '#1a1a2e' );
        setFontFamily( () => data.properties.font_family || '' );
        setCustomCss( () => data.custom_css || '' );
        // Delay setting the flag to allow the domains useEffect to skip the initial render
        setTimeout(() => {
          initialFetchDone.current = true;
        }, 100);
      }
    })
  }

  const addToDomains = () => {
    if(newDomain.split('.').length == 1) { return }

    setDomains( (prev) => prev.concat( [newDomain] ).filter(onlyUnique)  )
    setAddDomainFormHighlighed( () => false )
    setAddDomainFormVisible( () => false );
    setNewDomain( () => '' );
  }

  const removeFromDomains = (domain) => {
    setDomains( (prev) => prev.filter( (d) => d != domain ))
  }

  const domainWithHttp = (domain) => `https://${domain}`

  const domainsList = () => {
    return (<div>
      {domains.map( (domain) => {
        return <div key={domain}>
          <span className="domain-name"> {domainWithHttp(domain)} </span>
          <a href="#" className="btn" onClick={(e) => { e.preventDefault(); removeFromDomains(domain)} } >
            <i className="fa fa-trash-o" aria-hidden="true"></i>
            {t('quote_builder_widget.delete')}
          </a>
        </div>
      } )  }
    </div>)
  }

  const addDomainForm = () => {
    return (
      <div className="form-inline">
        <div className="form-group">
          <div className="input-group">
            <div className="input-group-addon"> https://
            </div>
            <input type="text" className="form-control" size="35" ref={domainInputRef} value={newDomain} placeholder="www.example.com" onChange={ (e) => setNewDomain( () => e.target.value ) }/>
          </div>
        </div>

        <button type="button" className="btn simple-btn" onClick={addToDomains}> {t('quote_builder_widget.save')} </button>
        <button className="btn" type="button" onClick={ () => setAddDomainFormVisible( () => false ) }> {t('quote_builder_widget.cancel')} </button>
    </div>)
  }

  const handleCancel = (e) => {
    e.preventDefault();
    setMode();
  }

  // Reusable save function that can be called with current or overridden values
  const saveWidget = (overrides = {}) => {
    const domainsToSave = overrides.domains !== undefined ? overrides.domains : domains;

    const saveData = {
      identifier: savedIdentifier,
      domains: domainsToSave,
      highlight_color: highlightColor,
      theme: selectedTheme,
    };

    // Include styling options for themes that support it
    if (supportsCustomCss) {
      saveData.background_color = backgroundColor;
      saveData.font_family = fontFamily;
      saveData.custom_css = overrides.customCss !== undefined ? overrides.customCss : customCss;
    }

    PostRequest({
      url: '/quote-builder/user-quote-builder-widgets',
      data: saveData,
      callback: (data) => {
        setSuccessMessage(t('quote_builder_widget.saved_successfully'));
        setPreviewKey(Date.now());
        if (!savedIdentifier) {
          setSavedIdentifier(data.identifier);
          window.history.replaceState({}, '', `/quote-builder/widgets/${data.identifier}/edit`);
        }
      },
      400: (data) => {
        setErrorMessage(data.error);
      }
    });
  };

  // Auto-save when domains change (after initial fetch)
  useEffect(() => {
    if (!initialFetchDone.current) {
      return;
    }
    if (domains.length === 0) {
      return;
    }
    saveWidget();
  }, [domains]);

  const handleSave = (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    if(domains.length == 0) {
      setAddDomainFormVisible( () => true )
      setAddDomainFormHighlighed( () => true )
      $(domainInputRef.current).focus();
      return;
    }

    saveWidget();
  }

  // useEffect
  useEffect(fetchWidget, [identifier])
  // useEffect

  return (<div>
    <div className="col-lg-12">

        <table className="table table-striped table-bordered-outside quote_builder_table">
          <tbody>
            <tr>
              <th colSpan="2"> { identifier ? t('quote_builder_widget.update_title') : t('quote_builder_widget.new_title') } </th>
            </tr>

            <tr>
              <td style={{ width: '560px' }}> {t('quote_builder_widget.domains')} <br /> <small> {t('quote_builder_widget.domains_info')} </small> </td>
              <td>
                <div style={ addDomainFormHighlighed ? { padding: "20px", border: "1px solid #ff0000" } : {}}>
                  {domainsList()}
                  <br />

                  <button className="btn simple-btn" onClick={ (e) => { e.preventDefault(); setAddDomainFormVisible( () => true ) } } >
                    <i className="fa fa-plus-square-o" aria-hidden="true"></i> {t('quote_builder_widget.add_domain')}
                  </button>
                  {addDomainFormVisible && <> <br /> <br /> {addDomainForm()} </>}
                </div>
              </td>
            </tr>

            <tr>
              <td> {t('quote_builder_widget.highlight_color')} <br /> <small>  {t('quote_builder_widget.highlight_color_info')} </small>  </td>
              <td> <input type="color" value={highlightColor} onChange={ (e) => setHighlightColor( () => e.target.value )  } /> </td>
            </tr>

            <tr>
              <td> {t('quote_builder_widget.theme')} <br /> <small> {t('quote_builder_widget.theme_info')} </small> </td>
              <td>
                <select
                  className="form-control"
                  value={selectedTheme}
                  onChange={(e) => setSelectedTheme(() => e.target.value)}
                  style={{ width: '200px' }}
                >
                  {availableThemes.map((themeName) => (
                    <option key={themeName} value={themeName}>
                      {themeName.charAt(0).toUpperCase() + themeName.slice(1)}
                    </option>
                  ))}
                </select>
              </td>
            </tr>

            {supportsCustomCss && (
              <tr>
                <td>
                  {t('quote_builder_widget.background_color')} <br />
                  <small>{t('quote_builder_widget.background_color_info')}</small>
                </td>
                <td>
                  <input
                    type="color"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(() => e.target.value)}
                  />
                </td>
              </tr>
            )}

            {supportsCustomCss && (
              <tr>
                <td>
                  {t('quote_builder_widget.font_family')} <br />
                  <small>{t('quote_builder_widget.font_family_info')}</small>
                </td>
                <td>
                  <select
                    className="form-control"
                    value={fontFamily}
                    onChange={(e) => setFontFamily(() => e.target.value)}
                    style={{ width: '200px' }}
                  >
                    {FONT_FAMILIES.map((font) => (
                      <option key={font.value} value={font.value} style={{ fontFamily: font.value || 'inherit' }}>
                        {font.label}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            )}

            {supportsCustomCss && (
              <tr>
                <td>
                  {t('quote_builder_widget.custom_css')} <br />
                  <small>{t('quote_builder_widget.custom_css_info')}</small>
                </td>
                <td>
                  <button
                    type="button"
                    className="btn simple-btn"
                    onClick={() => setIsCssEditorOpen(true)}
                  >
                    <i className="fa fa-code" aria-hidden="true"></i>{' '}
                    {customCss ? t('quote_builder_widget.edit_css') : t('quote_builder_widget.add_custom_css')}
                  </button>
                  {customCss && (
                    <span style={{ marginLeft: '10px', color: '#28a745' }}>
                      <i className="fa fa-check-circle" aria-hidden="true"></i>{' '}
                      {t('quote_builder_widget.custom_css_applied')}
                    </span>
                  )}
                </td>
              </tr>
            )}

            <tr>
              <td colSpan="2">
                {errorMessage != '' && <div className="alert alert-danger fade in"> <strong>  <i className="fa fa-exclamation-circle"></i> {errorMessage} </strong> </div>}
                {successMessage != '' && <div className="alert alert-success fade in"> <strong>  <i className="fa fa-check-circle"></i> {successMessage} </strong> </div>}
                <button className="btn simple-btn" type="button" onClick={handleSave}> {t('quote_builder_widget.save')} </button> &nbsp; &nbsp;
                <button className="btn" onClick={handleCancel}> {t('quote_builder_widget.cancel')} </button>
              </td>
            </tr>

          </tbody>
        </table>

        {savedIdentifier && (
          <div style={{ marginTop: '30px' }}>
            <h4>{t('quote_builder_widget.preview')}</h4>
            <hr />
            <div style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '10px', backgroundColor: '#fafafa' }}>
              <WidgetPreview
                identifier={savedIdentifier}
                templateIdentifier={SAMPLE_TEMPLATE_IDENTIFIER}
                previewKey={previewKey}
              />
            </div>
          </div>
        )}

    </div>

    {supportsCustomCss && (
      <CssEditorModal
        isOpen={isCssEditorOpen}
        onClose={() => setIsCssEditorOpen(false)}
        onSave={(newCss) => {
          setCustomCss(newCss);
          setIsCssEditorOpen(false);
          saveWidget({ customCss: newCss });
        }}
        currentCss={customCss}
        theme={selectedTheme}
        domains={domains}
      />
    )}

    </div>
  )
}

export default FormNew;
